// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { useState } from 'react';

interface CurrencyTaggedDataset {
  points: { isoCode: IsoCurrencyCode | '' }[];
}

interface CurrencySelectorState {
  availableCurrencies: IsoCurrencyCode[];
  activeCurrencyCode: IsoCurrencyCode | null;
  setSelectedCurrency: (value: IsoCurrencyCode) => void;
}

/**
 * Derives the currency-toggle state shared by the cost and time analytics charts.
 * Money cannot be summed across currencies, so charts render one at a time:
 * availableCurrencies are scanned across every dataset (excluding points with
 * no currency), and activeCurrencyCode resolves synchronously to the explicit selection
 * when still present, otherwise the first available — avoiding an empty-chart flash.
 */
export function useCurrencySelector(
  datasets: (CurrencyTaggedDataset | undefined)[]
): CurrencySelectorState {
  const [selectedCurrency, setSelectedCurrency] =
    useState<IsoCurrencyCode | null>(null);

  const codes = new Set<IsoCurrencyCode>();
  for (const dataset of datasets) {
    dataset?.points.forEach((point) => {
      if (point.isoCode) {
        codes.add(point.isoCode);
      }
    });
  }
  const availableCurrencies = Array.from(codes).sort();

  const activeCurrencyCode =
    selectedCurrency && availableCurrencies.includes(selectedCurrency)
      ? selectedCurrency
      : (availableCurrencies[0] ?? null);

  return { availableCurrencies, activeCurrencyCode, setSelectedCurrency };
}
